import { AuthBaseClient } from '../base-client';
import type {
  EmailResponse,
  EmailAttachmentResponse,
  UpdateAttachmentLabelsRequest,
  UpdateEmailLabelsRequest,
} from './types';

/**
 * Client for email API endpoints.
 */
export class EmailClient extends AuthBaseClient {
  /**
   * Get email by ID.
   */
  async getById(id: string): Promise<EmailResponse> {
    return this.get<EmailResponse>(`/api/emails/${id}`);
  }

  /**
   * Get attachments for an email.
   */
  async getAttachments(emailId: string): Promise<EmailAttachmentResponse[]> {
    return this.get<EmailAttachmentResponse[]>(
      `/api/email-attachments?emailId=${encodeURIComponent(emailId)}`
    );
  }

  /**
   * Get the API streaming URL for an attachment file.
   * The file is streamed through the API — no GCS signed URLs exposed.
   */
  getAttachmentFileUrl(attachmentId: string): string {
    return `${this.baseUrl}/api/email-attachments/${attachmentId}/file`;
  }

  /**
   * Replay an email: delete derived data and re-trigger processing pipeline.
   */
  async replay(id: string): Promise<EmailResponse> {
    return this.post<EmailResponse>(`/api/emails/${id}/replay`, {});
  }

  /**
   * Update labels on an attachment and record the correction for learning.
   */
  async updateAttachmentLabels(
    attachmentId: string,
    data: UpdateAttachmentLabelsRequest
  ): Promise<EmailAttachmentResponse> {
    return this.patch<EmailAttachmentResponse>(
      `/api/email-attachments/${attachmentId}/labels`,
      data
    );
  }

  /**
   * Update labels on an email and record the correction for learning.
   */
  async updateLabels(id: string, data: UpdateEmailLabelsRequest): Promise<EmailResponse> {
    return this.patch<EmailResponse>(`/api/emails/${id}/labels`, data);
  }
}
